import React, { useState } from "react";
import { Modal, ModalBody, PrimaryBlueButton } from "@velaro/velaro-component-lib";
import { AlertTriangle, IconClose, IconPlus } from "@velaro/velaro-component-lib";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";

interface BigCommerceConfig {
  id: number;
  siteId: number;
  displayName: string;
  storeHash: string;
  storeName: string;
  storeUrl: string;
  enabled: boolean;
  isActive?: boolean;
  disabledAt?: string | null;
  disabledBy?: string | null;
  createdAt: string;
  accessTokenMasked: string;
}

interface Props {
  configs: BigCommerceConfig[];
  onDelete(id: number): void;
  onRefresh(): void;
}

export default function BigCommerceAccounts({ configs, onDelete, onRefresh }: Props) {
  const navigate = useNavigate();
  const api = useApi();
  const [deleteTarget, setDeleteTarget] = useState<BigCommerceConfig | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);
  const [tokenEditTarget, setTokenEditTarget] = useState<BigCommerceConfig | null>(null);
  const [newToken, setNewToken] = useState("");
  const [tokenSaving, setTokenSaving] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);

  async function confirmDelete() {
    if (!deleteTarget) return;
    await api.admin.delete(`BigCommerce/Configs/${deleteTarget.id}`);
    onDelete(deleteTarget.id);
    setDeleteTarget(null);
  }

  async function saveDisplayName(config: BigCommerceConfig) {
    setSaving(true);
    try {
      await api.admin.put(`BigCommerce/Configs/${config.id}`, { displayName: editName });
      onRefresh();
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  }

  async function saveToken() {
    if (!tokenEditTarget || !newToken.trim()) return;
    setTokenSaving(true);
    setTokenError(null);
    try {
      const resp = await api.admin.put(`BigCommerce/Configs/${tokenEditTarget.id}`, { accessToken: newToken.trim() });
      const data = await resp.json();
      if (data?.connectionWarning) {
        setTokenError(`Token saved but connection test failed: ${data.connectionWarning}`);
      } else {
        setTokenEditTarget(null);
        setNewToken("");
        onRefresh();
      }
    } catch {
      setTokenError("Failed to update token. Please try again.");
    } finally {
      setTokenSaving(false);
    }
  }

  async function toggleEnabled(config: BigCommerceConfig) {
    await api.admin.put(`BigCommerce/Configs/${config.id}`, { enabled: !config.enabled });
    onRefresh();
  }

  async function toggleActive(config: BigCommerceConfig) {
    await api.admin.post(`BigCommerce/Configs/${config.id}/toggle-active`, {});
    onRefresh();
  }

  return (
    <div className="panel bg-white dark:bg-stone-300 rounded-2xl shadow-md mb-4 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="text-slate-800 text-lg font-medium">Accounts</div>
        <PrimaryBlueButton
          label="Add Account"
          onClick={() => navigate("/integrations/bigcommerce/create")}
          prefixIcon={React.createElement(IconPlus)}
          className="font-medium"
        />
      </div>

      {configs.length === 0 ? (
        <div className="py-8 text-center">
          <div className="text-slate-500 text-sm">No BigCommerce stores connected yet.</div>
          <div className="text-slate-400 text-xs mt-1">Click "Add Account" above to connect your store.</div>
        </div>
      ) : (
        <table className="table-fixed w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 text-xs border-b">
              <th className="pb-2 font-medium">Store</th>
              <th className="pb-2 font-medium">Store Hash</th>
              <th className="pb-2 font-medium">Status</th>
              <th className="pb-2 w-24"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {configs.map((config) => (
              <tr key={config.id} className="py-2">
                <td className="py-3 pr-4">
                  {editingId === config.id ? (
                    <div className="flex gap-2">
                      <input
                        className="input text-sm w-full"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveDisplayName(config); if (e.key === "Escape") setEditingId(null); }}
                        autoFocus
                      />
                      <button onClick={() => saveDisplayName(config)} disabled={saving} className="text-blue-600 text-xs px-2">Save</button>
                      <button onClick={() => setEditingId(null)} className="text-slate-400 text-xs px-1">✕</button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-800">{config.displayName || config.storeName || "BigCommerce"}</span>
                      <button
                        onClick={() => { setEditingId(config.id); setEditName(config.displayName); }}
                        className="text-slate-400 hover:text-slate-600 text-xs opacity-0 group-hover:opacity-100"
                      >✎</button>
                    </div>
                  )}
                  <div className="text-xs text-slate-400">{config.accessTokenMasked}</div>
                </td>
                <td className="py-3 pr-4 text-slate-500 text-xs font-mono">{config.storeHash || "—"}</td>
                <td className="py-3 pr-4">
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => toggleEnabled(config)}
                      className={`text-xs px-2 py-0.5 rounded-full ${config.enabled ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}
                    >
                      {config.enabled ? "Enabled" : "Disabled"}
                    </button>
                    <button
                      onClick={() => toggleActive(config)}
                      className={`text-xs px-2 py-0.5 rounded-full ${(config.isActive ?? true) ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}
                      title={(config.isActive ?? true) ? "Click to pause integration" : "Click to resume integration"}
                    >
                      {(config.isActive ?? true) ? "Active" : "Paused"}
                    </button>
                    {!(config.isActive ?? true) && config.disabledAt && (
                      <div className="text-xs text-amber-600 mt-0.5">
                        Paused {new Date(config.disabledAt).toLocaleDateString()}{config.disabledBy ? ` by ${config.disabledBy}` : ""}
                      </div>
                    )}
                  </div>
                </td>
                <td className="py-3 text-right">
                  <div className="flex flex-col gap-1 items-end">
                    <button
                      onClick={() => { setTokenEditTarget(config); setNewToken(""); setTokenError(null); }}
                      className="text-xs text-blue-500 hover:text-blue-700 px-2 py-1 rounded border border-blue-200 hover:border-blue-400"
                    >
                      Update Token
                    </button>
                    <button
                      onClick={() => setDeleteTarget(config)}
                      className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded border border-red-200 hover:border-red-400"
                    >
                      Remove
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {tokenEditTarget && (
        <Modal show={true}>
          <ModalBody>
            <div className="flex items-center justify-between mb-4">
              <span className="font-semibold text-slate-800">Update Access Token</span>
              <button onClick={() => setTokenEditTarget(null)}><IconClose className="w-6 text-slate-400" /></button>
            </div>
            <p className="text-sm text-slate-600 mb-1">
              Store: <strong>{tokenEditTarget.displayName || tokenEditTarget.storeName}</strong>
            </p>
            <p className="text-xs text-slate-400 mb-4">
              Current token: <span className="font-mono">{tokenEditTarget.accessTokenMasked}</span>
            </p>
            <label className="flex flex-col gap-1 mb-4">
              <span className="text-sm font-medium text-slate-700">New Access Token</span>
              <input
                type="password"
                className="input font-mono text-sm"
                placeholder="Paste your new BigCommerce access token"
                value={newToken}
                onChange={(e) => setNewToken(e.target.value)}
                autoFocus
              />
              <span className="text-xs text-slate-400">Generate a new token in BigCommerce → Settings → API → Store-level API accounts.</span>
            </label>
            {tokenError && <p className="text-sm text-red-600 mb-3">{tokenError}</p>}
            <div className="flex justify-end gap-3">
              <button onClick={() => setTokenEditTarget(null)} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-600 text-sm">Cancel</button>
              <button
                onClick={saveToken}
                disabled={tokenSaving || !newToken.trim()}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {tokenSaving ? "Saving…" : "Save & Test"}
              </button>
            </div>
          </ModalBody>
        </Modal>
      )}

      {deleteTarget && (
        <Modal show={true}>
          <ModalBody>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-6 text-red-500" />
                <span className="font-semibold text-slate-800">Remove Account</span>
              </div>
              <button onClick={() => setDeleteTarget(null)}><IconClose className="w-6 text-slate-400" /></button>
            </div>
            <p className="text-sm text-slate-600 mb-6">
              Remove <strong>{deleteTarget.displayName || "this account"}</strong>? This cannot be undone.
            </p>
            <div className="flex justify-center gap-3">
              <button onClick={() => setDeleteTarget(null)} className="px-5 py-2 rounded-lg border border-blue-500 text-blue-600 text-sm font-medium">Cancel</button>
              <button onClick={confirmDelete} className="px-5 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700">Remove</button>
            </div>
          </ModalBody>
        </Modal>
      )}
    </div>
  );
}
